import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CollapsibleSection } from "../../components/document/CollapsibleSection";
import { useAuth } from "../../context/AuthContext";
import { ROLE_LABELS } from "../../lib/rbac";
import { AccountBalanceTiles } from "./components/AccountBalanceTiles";
import { AiAccountantWidget } from "./components/AiAccountantWidget";
import { DashboardSessionHeader } from "./components/DashboardSessionHeader";
import { QuickStartChecklist } from "./components/QuickStartChecklist";
import { DashboardGettingStartedBanner } from "./components/DashboardGettingStartedBanner";
import { DashboardAnomaliesBanner } from "./components/DashboardAnomaliesBanner";
import { PinnedAccountBalancesGrid } from "./components/PinnedAccountBalancesGrid";
import {
  MiniDashboardPanel,
  dashboardCanProfitCharts,
} from "./components/MiniDashboardPanel";
import { useDashboardKpisQuery } from "./hooks/useDashboardKpisQuery";
import { currentMonthDashboardRange } from "./lib/date-range";
import { isEmptyOrgKpis } from "./lib/is-empty-org-kpis";
import { hasRole, FinanceAdminRoles, type RoleCodeValue } from "../../lib/rbac";

function formatOrganizationName(orgId: string): string {
  const normalized = orgId.trim();
  if (!normalized) {
    return "Global Enterprise";
  }

  const slug = normalized.replace(/^org_/i, "").replace(/[_-]+/g, " ").trim();
  if (!slug) {
    return normalized;
  }

  return slug.replace(/\b\w/g, (character) => character.toUpperCase());
}

function resolvePrimaryRoleLabel(
  roleLabels: string[],
  roles: number[],
): string {
  if (roleLabels.length > 0 && roleLabels[0]?.trim()) {
    return roleLabels[0];
  }

  const primaryRole = roles[0];
  if (primaryRole !== undefined && primaryRole in ROLE_LABELS) {
    return ROLE_LABELS[primaryRole as keyof typeof ROLE_LABELS];
  }

  return "SuperAdmin";
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();

  const defaultRange = useMemo(() => currentMonthDashboardRange(), []);

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    kpis,
    isLoading: isLoadingKpis,
    isRefreshing,
    error,
    lastUpdatedAt,
    refresh: refreshKpis,
  } = useDashboardKpisQuery(appliedRange.startDate, appliedRange.endDate);

  function handleApplyRange() {
    setAppliedRange({
      startDate: startDate.trim(),
      endDate: endDate.trim(),
    });
    setRefreshKey((value) => value + 1);
  }

  function handleResetRange() {
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setAppliedRange(defaultRange);
    setRefreshKey((value) => value + 1);
  }

  function handleRefreshAll() {
    refreshKpis();
    setRefreshKey((value) => value + 1);
  }

  if (!profile) {
    return null;
  }

  const organizationName =
    profile.companyName?.trim() || formatOrganizationName(profile.orgId);
  const roleLabel = resolvePrimaryRoleLabel(profile.roleLabels, profile.roles);
  const roles = (profile.roles ?? []) as RoleCodeValue[];
  const canProfitCharts = dashboardCanProfitCharts(roles);

  return (
    <div className="erp-page dash-home">
      <DashboardSessionHeader
        organizationName={organizationName}
        username={profile.displayName || profile.login}
        roleLabel={roleLabel}
      />

      <MiniDashboardPanel
        kpis={kpis}
        isLoading={isLoadingKpis}
        startDate={appliedRange.startDate}
        endDate={appliedRange.endDate}
        canProfitCharts={canProfitCharts}
        onRefresh={handleRefreshAll}
        isRefreshing={isRefreshing}
        lastUpdatedAt={lastUpdatedAt}
      />

      <section aria-label={t("dashboard.araseda_section")} className="dash-balances-row">
        <AccountBalanceTiles kpis={kpis} isLoading={isLoadingKpis} compact />
        <PinnedAccountBalancesGrid
          refreshKey={refreshKey}
          canWrite={hasRole(roles, ...FinanceAdminRoles)}
          compact
        />
      </section>

      {error ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
        >
          {error}
        </div>
      ) : null}

      <CollapsibleSection
        title={t("dashboard.section_tools", { defaultValue: "أدوات وبدء سريع" })}
        defaultOpen={false}
      >
        <QuickStartChecklist onRefreshKpis={handleRefreshAll} />
        {!isLoadingKpis && kpis && isEmptyOrgKpis(kpis) ? (
          <DashboardGettingStartedBanner />
        ) : null}
        <DashboardAnomaliesBanner />
        <AiAccountantWidget
          startDate={appliedRange.startDate}
          endDate={appliedRange.endDate}
          refreshKey={refreshKey}
        />
        <CollapsibleSection
          title={t("dashboard.section_date_range")}
          subtitle={t("dashboard.date_range_hint")}
          defaultOpen={false}
        >
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label htmlFor="dashboard-start-date" className="erp-label">
                {t("dashboard.filter_start_date")}
              </label>
              <input
                id="dashboard-start-date"
                type="text"
                dir="ltr"
                className="erp-input"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                placeholder="01/05/2026 00:00"
              />
            </div>
            <div>
              <label htmlFor="dashboard-end-date" className="erp-label">
                {t("dashboard.filter_end_date")}
              </label>
              <input
                id="dashboard-end-date"
                type="text"
                dir="ltr"
                className="erp-input"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                placeholder="31/05/2026 23:59"
              />
            </div>
            <div className="flex flex-wrap items-end gap-1 sm:col-span-2 lg:col-span-3">
              <button
                type="button"
                onClick={handleApplyRange}
                className="dash-refresh-btn"
              >
                {t("dashboard.apply_range")}
              </button>
              <button
                type="button"
                onClick={handleResetRange}
                className="rounded border border-emerald-300 bg-white px-3 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-50"
              >
                {t("dashboard.reset_range")}
              </button>
            </div>
          </div>
        </CollapsibleSection>
      </CollapsibleSection>
    </div>
  );
}
